import {
  env,
  AutoModel,
  AutoProcessor,
  RawImage,
  PreTrainedModel,
  Processor
} from "@huggingface/transformers";
import {
  initializeMobileOrtModel,
  processMobileImage,
  setMobileDiagnosticsHost,
  getMobileOrtModelInfo,
  type MobileDiagnosticsHost,
  type MobileRuntimeDiagnosticDetails,
} from "./mobile-ort-rmbg";

// Self-host ONNX Runtime Web wasm assets. With COEP: require-corp, cross-origin
// subresources (jsdelivr's default wasmPaths CDN) would be blocked because
// jsdelivr does not set Cross-Origin-Resource-Policy. scripts/copy-ort-wasm.mjs
// copies the assets from node_modules/onnxruntime-web/dist into public/ort/
// at build/install/dev time so they ship from our own origin.
//
// MUST be set before any AutoModel.from_pretrained() / pipeline() call.
if (env.backends?.onnx?.wasm) {
  env.backends.onnx.wasm.wasmPaths = "/ort/";
}

// Initialize different model configurations
const WEBGPU_MODEL_ID = "Xenova/modnet";
const FALLBACK_MODEL_ID = "briaai/RMBG-1.4";

interface ModelState {
  model: PreTrainedModel | null;
  processor: Processor | null;
  isWebGPUSupported: boolean;
  currentModelId: string;
  isIOS: boolean;
}

interface ModelInfo {
  currentModelId: string;
  isWebGPUSupported: boolean;
  isIOS: boolean;
}

interface RuntimePhaseRecord {
  phase: string;
  status: "start" | "end" | "error" | "timeout";
  t: string;
  elapsedMs?: number;
  context?: Record<string, unknown>;
}

interface RuntimeImageInfo {
  fileName?: string;
  fileType?: string;
  fileSize?: number;
  width?: number;
  height?: number;
}

export interface RuntimeDiagnosticDetails {
  failedPhase: string;
  elapsedMs: number;
  lastCompletedStep: string;
  modelSelected: string;
  isIOS: boolean;
  userAgent: string;
  crossOriginIsolated: boolean;
  sharedArrayBufferType: string;
  image?: RuntimeImageInfo;
  phaseHistory: RuntimePhaseRecord[];
  wasmProxy?: boolean;
  wasmPaths?: string;
  message: string;
}

class RuntimePhaseError extends Error {
  details: RuntimeDiagnosticDetails;

  constructor(message: string, details: RuntimeDiagnosticDetails, cause?: unknown) {
    super(message, { cause });
    this.name = "RuntimePhaseError";
    this.details = details;
  }
}

const MODEL_LOAD_TIMEOUT_MS = 120000;
const PROCESSOR_LOAD_TIMEOUT_MS = 60000;
const IMAGE_DECODE_TIMEOUT_MS = 15000;
const PREPROCESS_TIMEOUT_MS = 45000;
const TENSOR_CREATION_TIMEOUT_MS = 5000;
const INFERENCE_TIMEOUT_MS = 60000;
const POSTPROCESS_TIMEOUT_MS = 30000;
const CANVAS_BLOB_TIMEOUT_MS = 20000;

// iOS / iPadOS detection (including desktop-mode iPads)
const isIOS = () => {
  const userAgent = navigator.userAgent || "";
  const platform = navigator.platform || "";
  const maxTouchPoints = navigator.maxTouchPoints || 0;

  const hasIOSPlatform = /iPad|iPhone|iPod/.test(platform);
  const hasIOSUserAgent = /iPad|iPhone|iPod/.test(userAgent);
  const isIPadDesktopMode = platform === "MacIntel" && maxTouchPoints > 1;

  return hasIOSPlatform || hasIOSUserAgent || isIPadDesktopMode;
};

const state: ModelState = {
  model: null,
  processor: null,
  isWebGPUSupported: false,
  currentModelId: FALLBACK_MODEL_ID,
  isIOS: isIOS()
};

// Tracks the last runtime decision for diagnostics (exposed via getLastRuntimeInfo).
let lastRuntimeInfo: Record<string, unknown> = {};
let lastCompletedStep = "module loaded";
let phaseHistory: RuntimePhaseRecord[] = [];

function nowMs(): number {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }
  return Date.now();
}

function runtimeSnapshot(modelId = state.currentModelId): Record<string, unknown> {
  const wasm = env.backends?.onnx?.wasm;
  return {
    modelSelected: modelId,
    isIOS: state.isIOS,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    crossOriginIsolated:
      typeof globalThis.crossOriginIsolated === "boolean"
        ? globalThis.crossOriginIsolated
        : false,
    sharedArrayBufferType: typeof SharedArrayBuffer,
    wasmProxy: wasm?.proxy,
    wasmPaths: wasm?.wasmPaths,
  };
}

function trimPhaseHistory(): void {
  phaseHistory = phaseHistory.slice(-30);
}

function updateRuntimeInfo(info: Record<string, unknown>): void {
  lastRuntimeInfo = {
    ...lastRuntimeInfo,
    ...runtimeSnapshot(String(info.modelId ?? state.currentModelId)),
    ...info,
    lastCompletedStep,
    phaseHistory: [...phaseHistory],
  };
}

function startRuntimePhase(phase: string, context?: Record<string, unknown>): number {
  const startedAt = nowMs();
  phaseHistory.push({
    phase,
    status: "start",
    t: new Date().toISOString(),
    context,
  });
  trimPhaseHistory();
  updateRuntimeInfo({ phase, phaseStatus: "start", phaseStartedAt: startedAt, context });
  console.log(`[runtime] phase:start ${phase}`, context ?? "");
  return startedAt;
}

function completeRuntimePhase(
  phase: string,
  startedAt: number,
  context?: Record<string, unknown>,
): void {
  const elapsedMs = Math.round(nowMs() - startedAt);
  lastCompletedStep = phase;
  phaseHistory.push({
    phase,
    status: "end",
    t: new Date().toISOString(),
    elapsedMs,
    context,
  });
  trimPhaseHistory();
  updateRuntimeInfo({ phase, phaseStatus: "end", elapsedMs, context });
  console.log(`[runtime] phase:end ${phase} (${elapsedMs}ms)`, context ?? "");
}

function imageInfoFromContext(context?: Record<string, unknown>): RuntimeImageInfo | undefined {
  const image = context?.image;
  if (image && typeof image === "object") {
    return image as RuntimeImageInfo;
  }

  const info: RuntimeImageInfo = {};
  if (typeof context?.fileName === "string") info.fileName = context.fileName;
  if (typeof context?.fileType === "string") info.fileType = context.fileType;
  if (typeof context?.fileSize === "number") info.fileSize = context.fileSize;
  return Object.keys(info).length > 0 ? info : undefined;
}

function createRuntimePhaseError(
  phase: string,
  message: string,
  startedAt: number,
  context?: Record<string, unknown>,
  cause?: unknown,
  status: "error" | "timeout" = "error",
): RuntimePhaseError {
  const elapsedMs = Math.round(nowMs() - startedAt);
  phaseHistory.push({
    phase,
    status,
    t: new Date().toISOString(),
    elapsedMs,
    context,
  });
  trimPhaseHistory();
  const snapshot = runtimeSnapshot(String(context?.modelId ?? state.currentModelId));
  const details: RuntimeDiagnosticDetails = {
    failedPhase: phase,
    elapsedMs,
    lastCompletedStep,
    modelSelected: String(snapshot.modelSelected ?? state.currentModelId),
    isIOS: Boolean(snapshot.isIOS),
    userAgent: String(snapshot.userAgent ?? ""),
    crossOriginIsolated: Boolean(snapshot.crossOriginIsolated),
    sharedArrayBufferType: String(snapshot.sharedArrayBufferType ?? "undefined"),
    image: imageInfoFromContext(context),
    phaseHistory: [...phaseHistory],
    wasmProxy: typeof snapshot.wasmProxy === "boolean" ? snapshot.wasmProxy : undefined,
    wasmPaths: typeof snapshot.wasmPaths === "string" ? snapshot.wasmPaths : undefined,
    message,
  };
  updateRuntimeInfo({
    phase,
    phaseStatus: status,
    elapsedMs,
    errorDetails: details,
    context,
  });
  console.error(`[runtime] phase:${status} ${phase} (${elapsedMs}ms)`, details, cause);
  return new RuntimePhaseError(message, details, cause);
}

function isRuntimePhaseError(err: unknown): err is RuntimePhaseError {
  return err instanceof Error && err.name === "RuntimePhaseError" && "details" in err;
}

async function withPhaseTimeout<T>(
  phase: string,
  timeoutMs: number,
  action: () => Promise<T>,
  context?: Record<string, unknown>,
): Promise<T> {
  const startedAt = startRuntimePhase(phase, { ...context, timeoutMs });
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(
        createRuntimePhaseError(
          phase,
          `${phase} timed out after ${timeoutMs}ms`,
          startedAt,
          { ...context, timeoutMs },
          undefined,
          "timeout",
        ),
      );
    }, timeoutMs);
  });

  try {
    const result = await Promise.race([action(), timeout]);
    completeRuntimePhase(phase, startedAt, { ...context, timeoutMs });
    return result;
  } catch (err) {
    if (isRuntimePhaseError(err)) {
      throw err;
    }
    throw createRuntimePhaseError(
      phase,
      `${phase} failed: ${err instanceof Error ? err.message : String(err)}`,
      startedAt,
      { ...context, timeoutMs },
      err,
    );
  } finally {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
  }
}

export function getRuntimeErrorDetails(err: unknown): RuntimeDiagnosticDetails | undefined {
  let current: unknown = err;
  for (let i = 0; i < 5; i++) {
    if (isRuntimePhaseError(current)) {
      return current.details;
    }
    if (!(current instanceof Error)) {
      return undefined;
    }
    current = (current as Error & { cause?: unknown }).cause;
  }
  return undefined;
}

async function ensureModelReadyForProcessing(context: Record<string, unknown>): Promise<void> {
  // iOS path readiness is owned by the mobile-ort module — state.model /
  // state.processor remain null on iOS by design.
  if (state.isIOS) {
    if (getMobileOrtModelInfo().ready) {
      return;
    }
    await withPhaseTimeout(
      "model auto-reinitialize before processing",
      MODEL_LOAD_TIMEOUT_MS,
      () => initializeModel(FALLBACK_MODEL_ID).then(() => undefined),
      context,
    );
    return;
  }

  if (state.model && state.processor) {
    return;
  }

  await withPhaseTimeout(
    "model auto-reinitialize before processing",
    MODEL_LOAD_TIMEOUT_MS + PROCESSOR_LOAD_TIMEOUT_MS,
    () => initializeModel(state.currentModelId || FALLBACK_MODEL_ID).then(() => undefined),
    context,
  );
}

// ---------- Mobile (iOS direct-ORT) bridge ----------
//
// The iOS path is implemented in lib/mobile-ort-rmbg.ts and uses
// onnxruntime-web directly instead of @huggingface/transformers. This module
// continues to own runtime diagnostics state (lastRuntimeInfo, phaseHistory,
// lastCompletedStep) so the UI's failure-report builder works identically
// for both paths. We hand the mobile module a closure-based diagnostics host
// that funnels its phase events into our existing state.
const mobileDiagnosticsHost: MobileDiagnosticsHost = {
  startPhase: (phase, context) => startRuntimePhase(phase, context),
  endPhase: (phase, startedAt, context) => completeRuntimePhase(phase, startedAt, context),
  recordError: (phase, message, startedAt, context, cause, status) => {
    const err = createRuntimePhaseError(phase, message, startedAt, context, cause, status);
    return err.details as unknown as MobileRuntimeDiagnosticDetails;
  },
  setLastCompletedStep: (step) => {
    lastCompletedStep = step;
  },
};
setMobileDiagnosticsHost(mobileDiagnosticsHost);

// Initialize WebGPU with proper error handling
async function initializeWebGPU() {
  const gpu = (navigator as any).gpu;
  if (!gpu) {
    return false;
  }

  try {
    // Test if we can actually create an adapter
    const adapter = await gpu.requestAdapter();
    if (!adapter) {
      return false;
    }

    // Configure environment for WebGPU
    env.allowLocalModels = false;
    if (env.backends?.onnx?.wasm) {
      env.backends.onnx.wasm.proxy = false;
    }

    // Wait for WebAssembly initialization
    await new Promise(resolve => setTimeout(resolve, 100));

    // Initialize model with WebGPU
    state.model = await AutoModel.from_pretrained(WEBGPU_MODEL_ID, {
      device: "webgpu",
      config: {
        model_type: 'modnet',
        architectures: ['MODNet']
      }
    });
    state.processor = await AutoProcessor.from_pretrained(WEBGPU_MODEL_ID);
    state.isWebGPUSupported = true;
    return true;
  } catch (error) {
    console.error("WebGPU initialization failed:", error);
    return false;
  }
}

// Initialize the model based on the selected model ID
export async function initializeModel(forceModelId?: string): Promise<boolean> {
  const requested = forceModelId || FALLBACK_MODEL_ID;
  lastCompletedStep = "initializeModel:start";
  phaseHistory = [];
  updateRuntimeInfo({
    requested,
    isIOS: state.isIOS,
    stage: "initializeModel:start",
    modelId: requested,
  });
  try {
    console.log(
      `[runtime] initializeModel requested=${requested}, isIOS=${state.isIOS}`,
    );

    // iOS / mobile: delegate to the direct-ONNX-Runtime-Web pipeline in
    // lib/mobile-ort-rmbg.ts. We do NOT use @huggingface/transformers on iOS
    // because that path was identified in the 2026-04-24 audit as the likely
    // failure point on iPhone Safari (hangs at inference/session run).
    // Reference: docs/ai-audits/2026-04-24-onnx-fix-audit.md.
    if (state.isIOS) {
      console.log('[runtime] iOS detected — using direct-ORT RMBG (lib/mobile-ort-rmbg.ts)');
      updateRuntimeInfo({
        path: "iOS-direct-ort",
        modelId: FALLBACK_MODEL_ID,
      });
      await initializeMobileOrtModel();
      // Leave state.model / state.processor null on iOS — they are unused on
      // this path. processImage() detects state.isIOS and routes to
      // processMobileImage() instead.
      state.model = null;
      state.processor = null;
      state.currentModelId = FALLBACK_MODEL_ID;
      const mobileInfo = getMobileOrtModelInfo();
      updateRuntimeInfo({
        stage: "initializeModel:complete",
        modelId: FALLBACK_MODEL_ID,
        path: "iOS-direct-ort",
        mobileOrtReady: mobileInfo.ready,
        mobileOrtModelBytes: mobileInfo.modelBytes,
      });
      return true;
    }

    // Non-iOS flow remains the same
    const selectedModelId = forceModelId || FALLBACK_MODEL_ID;
    
    // Try WebGPU if requested
    if (selectedModelId === WEBGPU_MODEL_ID) {
      const webGPUSuccess = await initializeWebGPU();
      if (webGPUSuccess) {
        state.currentModelId = WEBGPU_MODEL_ID;
        return true;
      }
      // If WebGPU fails, fall through to fallback model without error
    }
    
    // Use fallback model
    env.allowLocalModels = false;
    if (env.backends?.onnx?.wasm) {
      env.backends.onnx.wasm.proxy = true;
    }
    console.log("[runtime] using non-iOS ONNX runtime with wasm.proxy=true");
    updateRuntimeInfo({
      path: "non-iOS",
      wasmProxy: true,
      modelId: FALLBACK_MODEL_ID,
    });

    state.model = await withPhaseTimeout(
      "model config/model fetch + model load",
      MODEL_LOAD_TIMEOUT_MS,
      () => AutoModel.from_pretrained(FALLBACK_MODEL_ID, {
        progress_callback: (progress) => {
          updateRuntimeInfo({
            phase: "model config/model fetch",
            modelProgress: progress,
            modelId: FALLBACK_MODEL_ID,
          });
          console.log("[runtime] model config/model fetch progress", progress);
        }
      }),
      { modelId: FALLBACK_MODEL_ID, path: "non-iOS" },
    );

    state.processor = await withPhaseTimeout(
      "processor fetch + processor load",
      PROCESSOR_LOAD_TIMEOUT_MS,
      () => AutoProcessor.from_pretrained(FALLBACK_MODEL_ID, {
        revision: "main",
        config: {
          do_normalize: true,
          do_pad: true,
          do_rescale: true,
          do_resize: true,
          image_mean: [0.5, 0.5, 0.5],
          feature_extractor_type: "ImageFeatureExtractor",
          image_std: [0.5, 0.5, 0.5],
          resample: 2,
          rescale_factor: 0.00392156862745098,
          size: { width: 1024, height: 1024 }
        }
      }),
      { modelId: FALLBACK_MODEL_ID, path: "non-iOS" },
    );

    state.currentModelId = FALLBACK_MODEL_ID;

    if (!state.model || !state.processor) {
      throw new Error("Failed to initialize model or processor");
    }

    state.currentModelId = selectedModelId;
    updateRuntimeInfo({
      stage: "initializeModel:complete",
      modelId: selectedModelId,
      path: "non-iOS",
    });
    return true;
  } catch (error) {
    console.error("Error initializing model:", error);
    if (forceModelId === WEBGPU_MODEL_ID) {
      console.log("Falling back to cross-browser model...");
      return initializeModel(FALLBACK_MODEL_ID);
    }
    if (isRuntimePhaseError(error)) {
      throw error;
    }
    throw createRuntimePhaseError(
      "initializeModel",
      error instanceof Error ? error.message : "Failed to initialize background removal model",
      nowMs(),
      { modelId: requested },
      error,
    );
  }
}

// Snapshot of the last runtime decision for diagnostics logging.
export function getLastRuntimeInfo(): Record<string, unknown> {
  return { ...lastRuntimeInfo };
}

// Get current model info
export function getModelInfo(): ModelInfo {
  return {
    currentModelId: state.currentModelId,
    isWebGPUSupported: Boolean((navigator as any).gpu),
    isIOS: state.isIOS
  };
}

export async function processImage(image: File): Promise<File> {
  const baseImageContext: Record<string, unknown> = {
    modelId: state.currentModelId,
    fileName: image.name,
    fileType: image.type,
    fileSize: image.size,
  };

  // iOS / mobile: delegate to the direct-ORT pipeline. Desktop continues
  // through the @huggingface/transformers path below — DO NOT alter it.
  if (state.isIOS) {
    try {
      await ensureModelReadyForProcessing({ ...baseImageContext, path: "iOS-direct-ort" });
      return await processMobileImage(image);
    } catch (error) {
      console.error("Error processing image (iOS direct-ORT):", error);
      if (isRuntimePhaseError(error)) {
        throw error;
      }
      // The mobile module throws MobileRuntimePhaseError on phase failures,
      // which already shares the RuntimePhaseError name and details shape;
      // isRuntimePhaseError catches them. Anything else gets wrapped here.
      throw createRuntimePhaseError(
        "processImage",
        "Failed to process image: " + (error instanceof Error ? error.message : String(error)),
        nowMs(),
        { ...baseImageContext, path: "iOS-direct-ort" },
        error,
      );
    }
  }

  let objectUrl: string | undefined;

  try {
    await ensureModelReadyForProcessing(baseImageContext);

    objectUrl = URL.createObjectURL(image);
    const img = await withPhaseTimeout(
      "image decode",
      IMAGE_DECODE_TIMEOUT_MS,
      () => RawImage.fromURL(objectUrl as string),
      baseImageContext,
    );

    const imageContext: Record<string, unknown> = {
      ...baseImageContext,
      image: {
        fileName: image.name,
        fileType: image.type,
        fileSize: image.size,
        width: img.width,
        height: img.height,
      },
    };

    const processorOutput = await withPhaseTimeout(
      "resize/preprocess",
      PREPROCESS_TIMEOUT_MS,
      () => state.processor!(img),
      imageContext,
    );

    const pixel_values = await withPhaseTimeout(
      "tensor creation",
      TENSOR_CREATION_TIMEOUT_MS,
      async () => {
        const values = (processorOutput as { pixel_values?: unknown }).pixel_values;
        if (!values) {
          throw new Error("Processor did not return pixel_values");
        }
        return values;
      },
      imageContext,
    );

    const { output } = await withPhaseTimeout(
      "inference/session run",
      INFERENCE_TIMEOUT_MS,
      () => state.model!({ input: pixel_values }),
      imageContext,
    );

    const maskData = await withPhaseTimeout(
      "mask/postprocess",
      POSTPROCESS_TIMEOUT_MS,
      async () => {
        if (!output?.[0]) {
          throw new Error("Model output did not include an alpha mask tensor");
        }
        return (
          await RawImage.fromTensor(output[0].mul(255).to("uint8")).resize(
            img.width,
            img.height,
          )
        ).data;
      },
      imageContext,
    );

    const blob = await withPhaseTimeout(
      "canvas/blob/download creation",
      CANVAS_BLOB_TIMEOUT_MS,
      async () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("Could not get 2d context");

        ctx.drawImage(img.toCanvas(), 0, 0);

        const pixelData = ctx.getImageData(0, 0, img.width, img.height);
        for (let i = 0; i < maskData.length; ++i) {
          pixelData.data[4 * i + 3] = maskData[i];
        }
        ctx.putImageData(pixelData, 0, 0);

        return new Promise<Blob>((resolve, reject) =>
          canvas.toBlob(
            (blob) => blob ? resolve(blob) : reject(new Error("Failed to create blob")),
            "image/png",
          ),
        );
      },
      imageContext,
    );
    
    const [fileName] = image.name.split(".");
    const processedFile = new File([blob], `${fileName}-nobg.png`, { type: "image/png" });
    return processedFile;
  } catch (error) {
    console.error("Error processing image:", error);
    if (isRuntimePhaseError(error)) {
      throw error;
    }
    throw createRuntimePhaseError(
      "processImage",
      "Failed to process image: " + (error instanceof Error ? error.message : String(error)),
      nowMs(),
      baseImageContext,
      error,
    );
  } finally {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
    }
  }
}

export async function processImages(images: File[]): Promise<File[]> {
  console.log("Processing images...");
  const processedFiles: File[] = [];
  
  for (const image of images) {
    const processedFile = await processImage(image);
    processedFiles.push(processedFile);
    console.log("Successfully processed image", image.name);
  }
  
  console.log("Processing images done");
  return processedFiles;
}
